import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { RefreshCw } from "lucide-react";
import { useI18n } from "../i18n";

/**
 * 有新版本時提醒重新整理
 * ═══════════════════════════════════════════════════════════════
 *
 * 單頁應用一旦載入就不會再換程式碼。部署新版之後，已經開著分頁的人
 * 手上仍然是舊的 JS —— 而且他完全不會知道。
 *
 * 這造成一種特別難查的情況：問題明明修好也上線了，使用者卻回報
 * 「還是一樣」。兩邊都沒說謊，只是他看的是幾小時前的程式。
 *
 * ── 怎麼判斷有新版 ──────────────────────────────────────────
 *
 * 抓一份最新的 index.html，比對裡面進入點的檔名。Vite 的檔名帶
 * 內容雜湊，所以「檔名不同」就等於「程式碼變了」——
 * 不需要另外維護版本號，也不會有人忘記更新它。
 *
 * ── 為什麼不自動重新整理 ────────────────────────────────────
 *
 * 因為那會把使用者打到一半的訊息、填到一半的表單直接清掉。
 * 一個突然自己跳掉的頁面比舊版程式更糟。所以只提示，按不按由他決定。
 *
 * ── 頻率 ────────────────────────────────────────────────────
 *
 * 每十分鐘一次，而且只在分頁可見時。這支請求只抓一份 HTML，
 * 但沒有必要在背景分頁裡持續打。
 */
const CHECK_INTERVAL = 10 * 60 * 1000;

export default function UpdatePrompt() {
  const { t } = useI18n();
  const [stale, setStale] = useState(false);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    // 目前這一份程式的進入點檔名
    const current = document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.src;
    if (!current) return;

    let timer: number | undefined;

    async function check() {
      if (document.visibilityState !== "visible" || stale) return;
      try {
        // no-store：這裡要的就是「線上現在是什麼」，快取會讓檢查失去意義
        const res = await fetch("/index.html", { cache: "no-store" });
        if (!res.ok) return;

        const html = await res.text();
        const match = html.match(/<script[^>]+type="module"[^>]+src="([^"]+)"/);
        if (!match) return;

        const latest = new URL(match[1], window.location.origin).href;
        if (latest !== current) setStale(true);
      } catch {
        /* 離線或暫時失敗。下一輪再試，不需要告訴使用者 */
      }
    }

    timer = window.setInterval(check, CHECK_INTERVAL);
    document.addEventListener("visibilitychange", check);
    return () => {
      if (timer !== undefined) window.clearInterval(timer);
      document.removeEventListener("visibilitychange", check);
    };
  }, [stale]);

  return (
    <AnimatePresence>
      {stale && (
        <motion.div
          initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ type: "spring", stiffness: 320, damping: 28 }}
          /* 放左下角，讓開右下角給 Toast 與回到頂端 */
          className="fixed bottom-4 left-4 z-50 flex items-center gap-3 rounded-2xl nm-lg px-4 py-3"
          role="status"
        >
          <span className="text-sm text-ink">{t("update.title")}</span>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-1.5 rounded-xl nm-pressable px-3 py-1.5 text-xs font-medium text-ink"
          >
            <RefreshCw className="size-3.5" />
            {t("update.reload")}
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
